import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Layout from '../../../components/Layout';
import api from '../../../api/axios';
import useAuthStore from '../../../store/authStore';
import { fmtDateTime, todayISO } from '../../../utils/dateFormat';
import SearchSelect from '../../../components/SearchSelect';
import { confirm } from '../../../components/ConfirmDialog';

const fmt   = (v) => Number(v || 0).toFixed(2);
const today = todayISO;

export default function Expenses() {
    const { user } = useAuthStore();
    const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

    const [expenseTypes, setExpenseTypes] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showTypeForm, setShowTypeForm] = useState(false);
    const [editType, setEditType] = useState(null);
    const [typeName, setTypeName] = useState('');
    const [showEntryForm, setShowEntryForm] = useState(false);
    const [entryForm, setEntryForm] = useState({ ref_id: '', amount: '', note: '' });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [typeRes, eRes] = await Promise.all([
                api.get('/other-expenses/expense-types'),
                api.get('/other-expenses/expenses'),
            ]);
            setExpenseTypes(typeRes.data.data || []);
            setExpenses(eRes.data.data || []);
        } catch { toast.error('Yüklənmədi.'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const saveType = async () => {
        if (!typeName.trim()) return toast.error('Ad tələb olunur.');
        try {
            if (editType) await api.put(`/other-expenses/expense-types/${editType.id}`, { name: typeName });
            else await api.post('/other-expenses/expense-types', { name: typeName });
            toast.success('Saxlandı.'); setShowTypeForm(false); load();
        } catch (err) { toast.error(err.response?.data?.msg || 'Xəta.'); }
    };

    const deleteType = async (t) => {
        if (!(await confirm(`"${t.name}" silinsin?`))) return;
        try { await api.delete(`/other-expenses/expense-types/${t.id}`); toast.success('Silindi.'); load(); }
        catch (err) { toast.error(err.response?.data?.msg || 'Xəta.'); }
    };

    const saveEntry = async () => {
        if (!entryForm.amount || Number(entryForm.amount) <= 0) return toast.error('Məbləğ daxil edin.');
        setSaving(true);
        try {
            await api.post('/other-expenses/expenses', { type_id: entryForm.ref_id || null, amount: entryForm.amount, note: entryForm.note });
            toast.success('Qeyd edildi.'); setShowEntryForm(false); load();
        } catch (err) { toast.error(err.response?.data?.msg || 'Xəta.'); }
        finally { setSaving(false); }
    };

    const deleteEntry = async (id) => {
        if (!(await confirm('Silinsin?'))) return;
        try { await api.delete(`/other-expenses/expenses/${id}`); toast.success('Silindi.'); load(); }
        catch (err) { toast.error(err.response?.data?.msg || 'Xəta.'); }
    };

    return (
        <Layout>
            <div className="p-4 max-w-4xl mx-auto">
                <h1 className="text-xl font-bold text-slate-800 mb-4">Xərclər</h1>

                {/* Expense types — collapsed */}
                <details className="bg-white rounded-2xl border border-slate-100 shadow-sm mb-4 group">
                    <summary className="cursor-pointer flex items-center justify-between p-4 list-none">
                        <div className="flex items-center gap-3">
                            <span className="text-sm font-semibold text-slate-700">📂 Xərc növləri</span>
                            <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{expenseTypes.length}</span>
                        </div>
                        <div className="flex items-center gap-3">
                            {isAdmin && (
                                <button onClick={e => { e.preventDefault(); e.stopPropagation(); setEditType(null); setTypeName(''); setShowTypeForm(true); }}
                                    className="text-xs text-blue-600 hover:text-blue-800 font-semibold">+ Əlavə et</button>
                            )}
                            <span className="text-slate-400 text-xs group-open:rotate-180 transition-transform">▼</span>
                        </div>
                    </summary>
                    <div className="px-4 pb-4 max-h-64 overflow-y-auto">
                        <div className="flex flex-wrap gap-2">
                            {expenseTypes.map(item => (
                                <div key={item.id} className="flex items-center gap-1 bg-slate-100 rounded-full px-3 py-1">
                                    <span className="text-xs text-slate-700">{item.name}</span>
                                    {isAdmin && <>
                                        <button onClick={() => { setEditType(item); setTypeName(item.name); setShowTypeForm(true); }}
                                            className="text-blue-400 hover:text-blue-600 text-xs ml-1">✎</button>
                                        <button onClick={() => deleteType(item)} className="text-red-300 hover:text-red-500 text-xs">×</button>
                                    </>}
                                </div>
                            ))}
                            {!expenseTypes.length && (
                                <p className="text-xs text-slate-400">
                                    {isAdmin ? 'Növ yoxdur. Əlavə edin. (benzin, internet...)' : 'Növ yoxdur.'}
                                </p>
                            )}
                        </div>
                    </div>
                </details>

                {/* Add button */}
                <div className="flex justify-end mb-4">
                    <button onClick={() => setShowEntryForm(true)}
                        className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">
                        + Yeni qeyd
                    </button>
                </div>

                {/* Entries */}
                {loading ? <p className="text-center text-slate-400 py-10">Yüklənir...</p> : (
                    <div className="grid gap-2">
                        {expenses.map(e => (
                            <div key={e.id} className="bg-white rounded-xl border border-slate-100 shadow-sm p-3 flex items-center gap-3">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="font-semibold text-slate-800">{fmt(e.amount)} ₼</span>
                                        {e.type_name && (
                                            <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{e.type_name}</span>
                                        )}
                                        <span className="text-xs text-slate-400">{fmtDateTime(e.created_at)}</span>
                                    </div>
                                    {e.note && <p className="text-xs text-slate-400 mt-0.5">📝 {e.note}</p>}
                                </div>
                                {isAdmin && (
                                    <button onClick={() => deleteEntry(e.id)}
                                        className="text-xs px-2 py-1 rounded-lg bg-red-50 hover:bg-red-100 text-red-500">Sil</button>
                                )}
                            </div>
                        ))}
                        {!expenses.length && <p className="text-center text-slate-400 py-10">Qeyd yoxdur.</p>}
                    </div>
                )}
            </div>

            {/* Type form modal */}
            {showTypeForm && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-xs shadow-xl">
                        <h2 className="font-bold text-slate-800 mb-4">{editType ? 'Növü düzəlt' : 'Yeni xərc növü'}</h2>
                        <input value={typeName} onChange={e => setTypeName(e.target.value)} placeholder="Növ adı (benzin, internet...)"
                            className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-blue-400" />
                        <div className="flex gap-2 mt-4">
                            <button onClick={() => setShowTypeForm(false)}
                                className="flex-1 py-2 rounded-xl border border-slate-200 text-sm text-slate-600">Ləğv et</button>
                            <button onClick={saveType}
                                className="flex-1 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold">Saxla</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Entry form modal */}
            {showEntryForm && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                        <h2 className="font-bold text-slate-800 mb-4">Xərc qeyd et</h2>
                        <div className="grid gap-3">
                            <div>
                                <label className="text-xs text-slate-500 mb-1 block">Növ</label>
                                <SearchSelect
                                    value={entryForm.ref_id}
                                    onChange={v => setEntryForm(f => ({ ...f, ref_id: v }))}
                                    options={expenseTypes.map(o => ({ value: o.id, label: o.name }))}
                                    placeholder="Seç (ixtiyari)"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-slate-500 mb-1 block">Məbləğ (₼)</label>
                                <input type="number" step="0.01" value={entryForm.amount}
                                    onChange={e => setEntryForm(f => ({ ...f, amount: e.target.value }))}
                                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-blue-400" />
                            </div>
                            <div>
                                <label className="text-xs text-slate-500 mb-1 block">Qeyd</label>
                                <input value={entryForm.note}
                                    onChange={e => setEntryForm(f => ({ ...f, note: e.target.value }))}
                                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-blue-400" />
                            </div>
                        </div>
                        <div className="flex gap-2 mt-4">
                            <button onClick={() => setShowEntryForm(false)}
                                className="flex-1 py-2 rounded-xl border border-slate-200 text-sm text-slate-600">Ləğv et</button>
                            <button onClick={saveEntry} disabled={saving}
                                className="flex-1 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold disabled:opacity-50">
                                {saving ? '...' : 'Qeyd et'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
}
